import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { CountryCode, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { DateTime } from 'luxon';
import { distinctUntilChanged, map, Subscription } from 'rxjs';
import { LocationState } from '../location/store/location.state';
import { SharedModule } from '../shared/shared.module';
import { AuthState } from '../shared/store/auth.state';
import { LangState } from '../shared/store/lang.state';
import { ProfileState } from './store/profile.state';

@Component({
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  imports: [ReactiveFormsModule, SharedModule, NgxsFormPluginModule],
})
export class ProfileComponent implements OnInit, OnDestroy {
  private readonly allowedPhoneCountries: CountryCode[] = ['BE', 'FR', 'DE', 'NL', 'GB'];

  readonly phoneCountries = this.allowedPhoneCountries.map((code) => ({
    code,
    callingCode: getCountryCallingCode(code),
  }));

  form = new UntypedFormGroup({
    firstName: new UntypedFormControl('', Validators.required),
    lastName: new UntypedFormControl('', Validators.required),
    gender: new UntypedFormControl(),
    zipCode: new UntypedFormControl('', Validators.required),
    city: new UntypedFormControl('', Validators.required),
    yearOfBirth: new UntypedFormControl(),
    hiddenYearOfBirth: new UntypedFormControl(),
    email: new UntypedFormControl('', [Validators.required, Validators.email]),
    phone: new UntypedFormControl(''),
  });

  termsForm = new UntypedFormGroup({
    privacyTermsAccept: new UntypedFormControl(false, Validators.requiredTrue),
    termsAccept: new UntypedFormControl(false, Validators.requiredTrue),
  });

  selectedPhoneCountry: CountryCode = 'BE';

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly auth: AuthState,
    private readonly fb: UntypedFormBuilder,
    private readonly lang: LangState,
    private readonly location: LocationState,
    readonly profile: ProfileState,
  ) {
    this.form.controls.phone.addValidators(this.validatePhone);
  }

  ngOnInit(): void {
    const state$ = this.profile.state$;
    if (!state$) {
      return;
    }

    this.subscriptions.add(
      state$
        .pipe(
          map((state) => state.current?.phone ?? ''),
          distinctUntilChanged(),
        )
        .subscribe((phone) => this.syncPhoneControl(phone)),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  saveProfile = (): void => {
    this.profile.updateProfile({
      ...this.form.value,
      id: this.profile.currentUserId,
      awsCognitoSub: this.auth.idToken.payload.sub,
      language: this.lang.lang.toUpperCase(),
      phone: this.getNormalizedPhone(),
      termsAccepted: true,
    });
  };

  cancel = (): void => {
    this.location.toHome();
  };

  closePicker = (selectedDate: DateTime, datepicker?: MatDatepicker<any>): void => {
    if (datepicker) {
      datepicker.close();
    }
    this.form.controls.hiddenYearOfBirth.setValue(selectedDate);
    this.form.controls.yearOfBirth.setValue(selectedDate.year);
    this.form.controls.yearOfBirth.markAsDirty();
  };

  onPhoneCountryChange = (country: CountryCode): void => {
    this.selectedPhoneCountry = this.isAllowedPhoneCountry(country) ? country : 'BE';
    this.form.controls.phone.updateValueAndValidity();
    this.formatPhoneInput();
  };

  formatPhoneInput = (): void => {
    const phone = this.form.controls.phone.value?.trim();
    if (!phone) {
      return;
    }

    const parsed = parsePhoneNumberFromString(phone, this.selectedPhoneCountry);
    if (parsed?.isValid()) {
      this.form.controls.phone.setValue(parsed.formatNational(), { emitEvent: false });
    }
    this.form.controls.phone.updateValueAndValidity({ emitEvent: false });
  };

  private readonly validatePhone = (control: AbstractControl): ValidationErrors | null => {
    const phone = control.value?.trim();
    if (!phone) {
      return null;
    }

    const parsed = parsePhoneNumberFromString(phone, this.selectedPhoneCountry);
    return parsed?.isValid() ? null : { phoneInvalid: true };
  };

  private syncPhoneControl(phone: string): void {
    if (!phone) {
      return;
    }

    const parsed = parsePhoneNumberFromString(phone);
    if (!parsed) {
      return;
    }

    if (parsed.country && this.isAllowedPhoneCountry(parsed.country)) {
      this.selectedPhoneCountry = parsed.country;
    }

    this.form.controls.phone.setValue(parsed.formatNational(), { emitEvent: false });
    this.form.controls.phone.updateValueAndValidity({ emitEvent: false });
  }

  private getNormalizedPhone(): string {
    const phone = this.form.controls.phone.value?.trim();
    if (!phone) {
      return '';
    }

    const parsed = parsePhoneNumberFromString(phone, this.selectedPhoneCountry);
    return parsed?.isValid() ? parsed.number : phone;
  }

  private isAllowedPhoneCountry(country: CountryCode): boolean {
    return this.allowedPhoneCountries.includes(country);
  }
}
